// src/pages/Simulation.jsx
import { useLoaderData, useLocation } from 'react-router-dom';
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import * as THREE from 'three';

// ⬇️ uPlot 버전 컴포넌트 (별도 파일)
import FloorViewer from './charts/FloorViewer';
import BuildingViewer from './charts/BuildingViewer';
import LinePlotGraph from './charts/uPlotGraph';
import ColorbarLegend from './charts/ColorbarLegend';
import MediaControls from './charts/MediaControls';
import FloorPicker, { ziFromFloor, qiFromMetric } from './charts/FloorPicker';

import { useAuth } from '../context/AuthContext';
import { requireUser } from '../context/Sessions';
import useIsMobile from '../utils/windows';

import styles from './Simulation.module.css';

/** ---------------------- Config ---------------------- */
const API_BASE = '/api';
const WS_PORT = 4001;
const defaultWsUrl = window.location.protocol === 'https:' ? `wss://${window.location.hostname}:${WS_PORT}/ws` : `ws://${window.location.hostname}:${WS_PORT}/ws`;

export function getWsUrl() {
    const cfg = window.__WS_CONFIG__ || {};
    return cfg.WS_URL || defaultWsUrl;
}

const DEFAULTS = { co: 1500, temp: 60, vis: 5 };
const HRR_PRESETS = [250, 500]; // kW/m^3
const DT = 3;
const LIMITED_FLOORS = ['B1', '3', '7'];

/** ---------------------- SessionStorage helpers ---------------------- */
const SIM_SS_KEY = (uid, modelId) => `sim:${uid ?? 'anon'}:${modelId ?? '-'}`;

function loadSimState(key) {
    try {
        const s = sessionStorage.getItem(key);
        return s ? JSON.parse(s) : null;
    } catch {
        return null;
    }
}
function saveSimState(key, obj) {
    try {
        sessionStorage.setItem(key, JSON.stringify(obj));
    } catch {}
}
function clearSimState(key) {
    try {
        sessionStorage.removeItem(key);
    } catch {}
}

/** ---------------------- Utilities ---------------------- */
function glbPathForFloor(f) {
    if (f === 'B2') return '/models/B2.glb';
    if (f === 'B1') return '/models/B1.glb';
    return `/models/F${f}.glb`;
}

function replayFloorToFloor(f) {
    if (f === 0) return 'B2';
    if (f === 1) return 'B1';
    return f - 1;
}

const METRIC_OPTIONS = [
    { key: 'co', label: 'CO농도' },
    { key: 'temp', label: '온도' },
    { key: 'vis', label: '가시거리' },
];
const fetchJSON = async (url, options) => {
    const res = await fetch(url, options);
    if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
    return res.json();
};

// Simple viridis-like colormap
function viridis(t) {
    const c = new THREE.Color().setHSL(0.67 - 0.67 * t, 1, 0.5);
    return [Math.round(c.r * 255), Math.round(c.g * 255), Math.round(c.b * 255)];
}

function getRangeForMetric(metric, params) {
    if (metric === 'co') return { minV: 0, maxV: Number(params.co) || 1500 };
    if (metric === 'temp') return { minV: 20, maxV: Number(params.temp) || 60 };
    if (metric === 'vis') return { minV: Number(params.vis) || 5, maxV: 30 };
    return { minV: 0, maxV: 1 };
}

function makeHeatmapTextureFromInt16({ data, width, height, minV, maxV, yFlip = true, alpha = 200, invert = false, transparentZero = true }) {
    const len = width * height;
    const rgba = new Uint8Array(len * 4);
    const inv = 1 / Math.max(1e-6, maxV - minV);

    for (let y = 0; y < height; y++) {
        const sy = yFlip ? height - 1 - y : y;
        for (let x = 0; x < width; x++) {
            const v = data[y * width + x];
            let t = Math.min(1, Math.max(0, (v - minV) * inv));
            if (invert) t = 1 - t;
            const [r, g, b] = viridis(t);
            const o = (sy * width + x) * 4;
            rgba[o + 0] = r;
            rgba[o + 1] = g;
            rgba[o + 2] = b;

            let isTransparent = false;
            if (transparentZero) {
                if (invert ? v >= maxV : v <= minV) {
                    isTransparent = true;
                }
            }
            rgba[o + 3] = isTransparent ? alpha : 255;
        }
    }

    const tex = new THREE.DataTexture(rgba, width, height, THREE.RGBAFormat);
    tex.needsUpdate = true;
    tex.flipY = false;
    tex.magFilter = THREE.LinearFilter;
    tex.minFilter = THREE.LinearMipMapLinearFilter;
    tex.generateMipmaps = false;
    tex.wrapS = THREE.ClampToEdgeWrapping;
    tex.wrapT = THREE.ClampToEdgeWrapping;
    return tex;
}

/** ---------------------- WebSocket hook ---------------------- */
const WS = { ws: null, listeners: new Set(), manualClose: false };

async function ensureWsConnection(user) {
    if (WS.ws && (WS.ws.readyState === WebSocket.OPEN || WS.ws.readyState === WebSocket.CONNECTING)) {
        return WS.ws;
    }

    const tenant = encodeURIComponent(user?.tenant_id ?? '');
    const uid = encodeURIComponent(user?.id ?? '');
    WS.manualClose = false;

    const url = `${getWsUrl()}?tenant=${tenant}&user=${user?.id}`;
    WS.ws = new WebSocket(url);
    WS.ws.binaryType = 'arraybuffer';

    // 공용 메시지 디스패치
    WS.ws.onmessage = (ev) => {
        WS.listeners.forEach((cb) => {
            try {
                cb(ev);
            } catch {}
        });
    };

    const redirectToLogin = () => {
        if (WS.manualClose) return;
        try {
            window.location.assign('/login');
        } catch {
            window.location.href = '/login';
        }
    };

    WS.ws.onclose = (ev) => {
        const authCloseCodes = new Set([4401, 4403, 4001, 1008]);
        const isAuthClose = authCloseCodes.has(ev.code) || /auth|unauth|token|expired|forbid/i.test(ev.reason || '');
        if (isAuthClose) {
            redirectToLogin();
            return;
        }
    };

    WS.ws.onerror = () => {
        // noop (onclose에서 최종 처리)
    };

    await new Promise((resolve) => {
        if (WS.ws.readyState === WebSocket.OPEN) return resolve();
        WS.ws.addEventListener('open', resolve, { once: true });
        setTimeout(resolve, 300);
    });
    return WS.ws;
}

function addWsListener(cb) {
    WS.listeners.add(cb);
    return () => WS.listeners.delete(cb);
}
function wsSend(objOrBuffer) {
    const ws = WS.ws;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    if (objOrBuffer instanceof ArrayBuffer) ws.send(objOrBuffer);
    else ws.send(JSON.stringify(objOrBuffer));
}

function useSimulationSocket(user, onMessage) {
    const cbRef = useRef(onMessage);
    useEffect(() => {
        cbRef.current = onMessage;
    }, [onMessage]);

    useEffect(() => {
        const stableHandler = (ev) => {
            try {
                cbRef.current?.(ev);
            } catch {}
        };
        ensureWsConnection(user);
        const off = addWsListener(stableHandler);
        return () => off();
    }, [user]);

    return { send: wsSend };
}

/** ---------------------- Loader ---------------------- */
export async function loader({ request }) {
    const user = await requireUser();
    await ensureWsConnection(user);

    const url = new URL(request.url);
    const modelId = url.searchParams.get('modelId');
    if (!modelId) {
        throw new Response('modelId is required', { status: 400 });
    }

    return { modelId };
}

/** ---------------------- Page ---------------------- */
export default function SimulationPage() {
    const { modelId } = useLoaderData();
    const { user } = useAuth();
    const simTopRef = useRef(null);

    const isMobile = useIsMobile();

    // 층 잠금 상태 (false: B1/3/7만, true: 전체)
    const [floorsUnlocked, setFloorsUnlocked] = useState(false);

    const [fireAlarm, setFireAlarm] = useState(null);
    const alarmActiveRef = useRef(false);

    const [floor, setFloor] = useState('B1');
    const [picked, setPicked] = useState(null);
    const [params, setParams] = useState(DEFAULTS);
    const [hrr, setHrr] = useState(HRR_PRESETS[0]);

    const [phase, setPhase] = useState('input'); // input | running | ready
    const phaseRef = useRef(phase);
    useEffect(() => {
        phaseRef.current = phase;
    }, [phase]);

    const [simulationId, setSimulationId] = useState(null);
    const simulationIdRef = useRef(null);
    useEffect(() => {
        simulationIdRef.current = simulationId;
    }, [simulationId]);

    const [activeTab, setActiveTab] = useState('result'); // result | evac
    const [metric, setMetric] = useState('co');

    const [viewerMode, setViewerMode] = useState('building');
    const [viewerView, setViewerView] = useState(null);

    // Media
    const [time, setTime] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [speed, setSpeed] = useState(1);
    const autoStartedRef = useRef(false);

    // Data
    const [charts, setCharts] = useState([]); // [{t, co, temp, vis}]
    const [miniOverlays, setMiniOverlays] = useState({
        co: {},
        temp: {},
        vis: {},
    });

    const location = useLocation();
    const replayState = location.state?.replay;
    const replayRef = useRef(null);

    const [overlayVisible, setOverlayVisible] = useState(false);
    const [currentTexture, setCurrentTexture] = useState(null);
    const [routesPayload, setRoutesPayload] = useState(null);
    const [controlledByFloor, setControlledByFloor] = useState({});

    const [routeGuide, setRouteGuide] = useState(null);
    const allServerRoutesRef = useRef([]);

    const [mobileGraphTab, setMobileGraphTab] = useState('co');
    const [legendOpen, setLegendOpen] = useState(false);

    const ziRef = useRef(ziFromFloor(floor));
    const qiRef = useRef(qiFromMetric(metric));
    useEffect(() => {
        ziRef.current = ziFromFloor(floor);
    }, [floor]);
    useEffect(() => {
        qiRef.current = qiFromMetric(metric);
    }, [metric]);

    const simKey = SIM_SS_KEY(user?.id, modelId);
    const renderMeasureRef = useRef({ start: 0, frameIndex: null });

    // 새로고침 판별 → 초기 1회만 결정
    function isPageReload() {
        try {
            const nav = performance.getEntriesByType('navigation')[0];
            return !!nav && nav.type === 'reload';
        } catch {
            return window.performance && window.performance.navigation && window.performance.navigation.type === 1;
        }
    }
    const restoreAllowedRef = useRef(isPageReload());

    // 오른쪽 패널 슬라이드/확장
    const [rightCollapsed, setRightCollapsed] = useState(false);
    const locked = phase !== 'input';
    const showExpandHandle = locked;

    // uPlot용 x/y 배열
    const xArr = useMemo(() => charts.map((d) => d.t * DT), [charts]);
    const coArr = useMemo(() => charts.map((d) => d.co), [charts]);
    const tpArr = useMemo(() => charts.map((d) => d.temp), [charts]);
    const vsArr = useMemo(() => charts.map((d) => d.vis), [charts]);

    const handleAutoStartAndTime = (frameIndex) => {
        if (!autoStartedRef.current) {
            autoStartedRef.current = true;
            setIsPlaying(true);
            if (simulationId) wsSend({ action: 'set_playing', simulationId, playing: true });
        }
        setTime((prev) => (typeof frameIndex === 'number' && frameIndex >= 0 ? frameIndex * DT : prev));
    };

    useEffect(() => {
        if (!fireAlarm?.message || isMobile) return;
        const timer = setTimeout(() => {
            setFireAlarm((prev) => {
                if (prev && prev.id === fireAlarm.id) {
                    alarmActiveRef.current = false; // 🔹 PC 알림 종료
                    return null;
                }
                return prev;
            });
        }, 3000);
        return () => clearTimeout(timer);
    }, [fireAlarm, isMobile]);

    const handleFireAlarmDone = useCallback(() => {
        alarmActiveRef.current = false;
        setFireAlarm(null);
    }, []);

    function handleRouteSelection(key) {
        if (!key) return;

        const routeKey = Number(key);
        const routes = allServerRoutesRef.current;

        let matched = routes.find((r) => r.key === routeKey);
        if (!matched) {
            console.warn('일치하는 경로 안내를 찾지 못했습니다.', {
                key,
                routes,
            });
            return;
        }

        const steps = Array.isArray(matched.steps) ? matched.steps : [];

        setRouteGuide({
            startLabel: matched.startLabel,
            endLabel: matched.endLabel,
            totalDistance: matched.totalDistance ?? steps.reduce((s, st) => s + (st.distance || 0), 0),
            steps,
        });
    }

    // WS
    const { send } = useSimulationSocket(user, (ev) => {
        if (typeof ev.data === 'string') {
            try {
                const msg = JSON.parse(ev.data);

                // 🔥 화재 알림
                if (msg.type === 'fire_alarm') {
                    if (msg.simulationId && simulationIdRef.current && msg.simulationId !== simulationIdRef.current) {
                        return;
                    }

                    if (alarmActiveRef.current) {
                        return;
                    }

                    setFireAlarm((prev) => {
                        const nextId = (prev?.id ?? 0) + 1;
                        const next = {
                            id: nextId,
                            message: msg.message ?? '',
                        };
                        alarmActiveRef.current = true; // 이제 알람이 표시 중
                        return next;
                    });
                    return;
                }
            } catch {}
        } else if (ev.data instanceof ArrayBuffer) {
            try {
                const now = performance.now();
                renderMeasureRef.current.start = now;
                // console.log('[Measure] WS frame received at', now.toFixed(1), 'ms');

                const buf = ev.data;
                const dv = new DataView(buf);
                const headerLen = dv.getUint32(0, true);
                const header = JSON.parse(new TextDecoder().decode(new Uint8Array(buf, 4, headerLen)));
                const align4 = (n) => (n + 3) & ~3;

                renderMeasureRef.current.frameIndex = header.index ?? null;

                let offset = 4 + headerLen;
                if (headerLen % 2 === 1) offset += 1;

                if (typeof header.index === 'number' && header.index === 0) {
                    if (streamTimeoutRef.current) {
                        clearTimeout(streamTimeoutRef.current);
                        streamTimeoutRef.current = null;
                    }
                    setPhase('ready');
                    setMetric('co');
                }

                if (header.stop) {
                    setIsPlaying(false);
                }

                const isRestoring = restoreAllowedRef.current;
                const isReplay = !!replayRef.current;
                if (!isRestoring && !isReplay && phaseRef.current === 'input') {
                    return;
                }

                const headerSimId = header.simulationId || header.simId || header.id;
                if (headerSimId && simulationIdRef.current && headerSimId !== simulationIdRef.current) {
                    return;
                }

                if (header.type === 'bundle' || header.type === 'contour') {
                    const contoursMeta = header.contours;
                    const texByMetric = {};
                    for (let k = 0; k < contoursMeta.length; k++) {
                        const meta = contoursMeta[k];
                        const { qi, metric, width, height } = meta;
                        const count = width * height;
                        const contour = new Int16Array(buf, offset, count);
                        offset += count * 2;

                        const invert = metric === 'vis';
                        const { minV, maxV } = getRangeForMetric(metric, params);

                        const tex = makeHeatmapTextureFromInt16({
                            data: contour,
                            width,
                            height,
                            minV,
                            maxV,
                            yFlip: false,
                            alpha: 120,
                            invert,
                            transparentZero: true,
                        });

                        texByMetric[metric] = tex;

                        if (qi === qiRef.current) {
                            setCurrentTexture(tex);
                            setOverlayVisible(true);
                        }
                    }

                    const curFloor = floor;
                    setMiniOverlays((prev) => {
                        const next = { ...prev };
                        for (const metric of Object.keys(texByMetric)) {
                            const bucket = { ...(next[metric] || {}) };
                            const prevTex = bucket[curFloor];
                            if (prevTex?.dispose) {
                                try {
                                    prevTex.dispose();
                                } catch {}
                            }
                            bucket[curFloor] = texByMetric[metric];
                            next[metric] = bucket;
                        }
                        return next;
                    });

                    const nextIdx = typeof header.index === 'number' ? header.index + 1 : 0;
                    handleAutoStartAndTime(nextIdx);
                }

                if (header.type === 'bundle' || header.type === 'graphs') {
                    const gdefs = header.graphs || [];
                    const series = [];
                    for (let i = 0; i < gdefs.length; i++) {
                        const len = gdefs[i].len | 0;
                        const arr = new Int16Array(buf, offset, len);
                        offset += len * 2;
                        series.push(arr);
                    }
                    if (series.length >= 3) {
                        const Nt = Math.min(series[0].length, series[1].length, series[2].length);
                        setCharts((prev) => {
                            let next = prev;
                            if (!Array.isArray(prev) || prev.length !== Nt) {
                                next = new Array(Nt).fill(0).map((_, t) => ({
                                    t,
                                    co: 0,
                                    temp: 0,
                                    vis: 0,
                                }));
                            }
                            for (let t = 0; t < Nt; t++) {
                                const row = next[t];
                                row.t = t;
                                row.co = series[0][t];
                                row.temp = series[1][t];
                                row.vis = series[2][t];
                            }
                            return next.slice();
                        });
                    }
                }

                if (header.type === 'bundle' || header.type === 'routes') {
                    offset = align4(offset);
                    const routes = header.routes || {};
                    const order = routes.order || [];
                    const floorsHdr = routes.floors || {};
                    const originMap = routes.originMap || null;

                    const collectedGuides = [];

                    const floorsObj = {};
                    for (const f of order) {
                        offset = align4(offset);
                        const meta = floorsHdr[f] || {};
                        const P = meta.positions | 0,
                            S = meta.segments | 0,
                            F = meta.flows | 0;
                        const O = meta.origin | 0;

                        const posView = new Float32Array(buf, offset, P * 3);
                        const pos = new Float32Array(posView.length);
                        pos.set(posView);
                        offset += P * 3 * 4;

                        const segView = new Uint32Array(buf, offset, S * 2);
                        const seg = new Uint32Array(segView.length);
                        seg.set(segView);
                        offset += S * 2 * 4;

                        let flows = null;
                        if (F > 0) {
                            const flowsView = new Float32Array(buf, offset, F);
                            flows = new Float32Array(flowsView.length);
                            flows.set(flowsView);
                            offset += F * 4;
                        }

                        let origins = null;
                        if (O > 0) {
                            const originsView = new Uint8Array(buf, offset, O);
                            origins = new Uint8Array(originsView.length);
                            origins.set(originsView);
                            offset += O;
                            offset = align4(offset);
                        }
                        floorsObj[f] = {
                            positions: pos,
                            segments: seg,
                            flows,
                            origins,
                        };

                        const guides = Array.isArray(meta.guides) ? meta.guides : [];
                        const originCode = originMap ? originMap[f] : null;

                        for (const g of guides) {
                            collectedGuides.push({
                                key: originCode ?? f,
                                startLabel: g.startLabel,
                                endLabel: g.endLabel,
                                totalDistance: g.totalDistance,
                                steps: g.steps || [],
                            });
                        }
                    }
                    allServerRoutesRef.current = collectedGuides;

                    const payload = {
                        floors: floorsObj,
                        maxFlow: Number(routes.maxFlow) || 1,
                        originMap,
                    };
                    setRoutesPayload(payload);
                }

                if (header.type === 'bundle' && header.controls) {
                    const floors = header.controls.floors || {};
                    const next = {};
                    for (const f of Object.keys(floors)) {
                        const ids = Array.isArray(floors[f]?.ids) ? floors[f].ids.map((n) => n | 0) : [];
                        next[f] = ids;
                    }
                    setControlledByFloor(next);
                } else {
                    setControlledByFloor({});
                }
                // console.log('web socket data processing finish.');
            } catch (e) {
                console.warn('WS binary parse error:', e);
            }
        }
    });

    // 뒤로가기/페이지 이동 시 WebSocket 정리
    useEffect(() => {
        return () => {
            try {
                WS.manualClose = true;
                if (WS.ws && (WS.ws.readyState === WebSocket.OPEN || WS.ws.readyState === WebSocket.CONNECTING)) {
                    WS.ws.close(1000, 'leave simulation page');
                }
            } catch {}
        };
    }, []);

    // simulationId 바뀌면 클라이언트 상태 일부 초기화
    useEffect(() => {
        if (!simulationId) return;
        if (restoreAllowedRef.current) return;

        setCharts([]);
        setTime(0);
        setIsPlaying(false);
        autoStartedRef.current = false;
    }, [simulationId]);

    // speed → 서버 반영
    useEffect(() => {
        if (!simulationId || phase !== 'ready') return;
        const serverSpeed = Math.max(1, Math.round(speed));
        send({ action: 'set_speed', simulationId, speed: serverSpeed });
    }, [speed, simulationId, phase, send]);

    // 대시보드 ‘다시보기’: 자동 실행
    useEffect(() => {
        if (!replayState) return;

        replayRef.current = replayState;

        setFloorsUnlocked(true);

        const nextFloor = replayState.floor ? replayFloorToFloor(replayState.floor) : 'B1';
        setFloor(String(nextFloor));

        setParams((prev) => ({ ...prev, ...(replayState.thresholds || {}) }));
        setMetric(replayState.metric ?? 'co');
        setHrr(replayState.hrr_density ?? 250);

        if (Number.isFinite(replayState.x) && Number.isFinite(replayState.y)) {
            setPicked({ x: replayState.x, y: -replayState.y });
        }

        const id = requestAnimationFrame(() => replaySimulation({ fromReplay: true }));
        return () => cancelAnimationFrame(id);
    }, [replayState]);

    const streamTimeoutRef = useRef(null);
    const STREAM_START_TIMEOUT_MS = 10000; // 10초
    const WS_CONNECT_TIMEOUT_MS = 2000; // 한 번의 연결 시도 최대 대기
    const WS_CONNECT_RETRIES = 3; // 재시도 횟수
    const WS_CONNECT_RETRY_DELAY = 300; // 재시도 간격(ms)

    function waitWsOpen(ws, timeoutMs) {
        return new Promise((resolve, reject) => {
            if (!ws) return reject(new Error('NO_WS'));
            if (ws.readyState === WebSocket.OPEN) return resolve();

            const onOpen = () => cleanup(resolve);
            const onClose = () => cleanup(() => reject(new Error('WS_CLOSED')));
            const onError = () => cleanup(() => reject(new Error('WS_ERROR')));

            const cleanup = (cb) => {
                clearTimeout(timer);
                ws.removeEventListener('open', onOpen);
                ws.removeEventListener('close', onClose);
                ws.removeEventListener('error', onError);
                if (cb) cb();
            };

            ws.addEventListener('open', onOpen, { once: true });
            ws.addEventListener('close', onClose, { once: true });
            ws.addEventListener('error', onError, { once: true });

            const timer = setTimeout(() => {
                cleanup(() => reject(new Error('WS_TIMEOUT')));
            }, timeoutMs);
        });
    }

    async function ensureWsReadyWithRetry(user) {
        let lastErr = null;

        for (let i = 0; i <= WS_CONNECT_RETRIES; i++) {
            try {
                // 기존 ensureWsConnection 재사용
                const ws = await ensureWsConnection(user);

                await waitWsOpen(ws, WS_CONNECT_TIMEOUT_MS);

                console.log('[WS] connected');
                return ws;
            } catch (e) {
                lastErr = e;
                console.warn(`[WS] connect attempt ${i + 1} failed:`, e.message);

                try {
                    WS.manualClose = true;
                    WS.ws?.close();
                } catch {}

                await new Promise((r) => setTimeout(r, WS_CONNECT_RETRY_DELAY));
            }
        }

        throw lastErr || new Error('WS_CONNECT_FAILED');
    }

    async function startSimulation(opts = {}) {
        if (isMobile) scrollToTopSmooth();

        const r = replayRef.current;
        const hasReplayCoords = !!(r && Number.isFinite(r.x) && Number.isFinite(r.y));
        if (!picked && !hasReplayCoords) {
            alert('평면도에서 좌표를 먼저 선택하세요.');
            return;
        }

        // ✅ WS 연결 보장 + 재시도
        try {
            await ensureWsReadyWithRetry(user);
        } catch (e) {
            alert('서버에 연결할 수 없습니다.\n' + '네트워크 상태 또는 브라우저 환경을 확인한 후 다시 시도해 주세요.');
            return;
        }

        setPhase('running');
        setRightCollapsed(true);

        // 기존 타임아웃 제거
        if (streamTimeoutRef.current) {
            clearTimeout(streamTimeoutRef.current);
            streamTimeoutRef.current = null;
        }

        // "첫 프레임 타임아웃" 설정
        streamTimeoutRef.current = setTimeout(() => {
            console.warn('Simulation stream timeout');
            setPhase('input');
            setRightCollapsed(false);
            setIsPlaying(false);
            alert('시뮬레이션 결과 수신에 실패했습니다.');
        }, STREAM_START_TIMEOUT_MS);

        try {
            const useFloor = r?.floor ?? floor;
            const useMetric = r?.metric ?? metric;

            const body = {
                x: hasReplayCoords ? r.x : picked.x,
                y: hasReplayCoords ? r.y : -picked.y,
                z: ziFromFloor(useFloor),
                q: qiFromMetric(useMetric),
                hrr_density: r?.hrr_density ?? hrr,
                thresholds: r?.thresholds ?? params,
                model_id: r?.model_id ?? modelId,
            };

            const start = performance.now();
            // console.log(`[Measure] Simulation start at ${start.toFixed(1)} ms`);
            const res = await fetchJSON(`${API_BASE}/simulation/start`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
            const end = performance.now();
            const dt = end - start;
            // console.log(`[Measure] Simulation end at ${dt.toFixed(1)} ms`);

            if (res.status && res.status !== 0) {
                if (streamTimeoutRef.current) {
                    clearTimeout(streamTimeoutRef.current);
                    streamTimeoutRef.current = null;
                }
                setPhase('input');
                setRightCollapsed(false);

                if (res.error === 'WEBSOCKET_NOT_CONNECTED') {
                    alert('시뮬레이션 서버와 연결되지 않아 시뮬레이션 결과를 표시할 수 없습니다.\n다른 브라우저 또는 네트워크 환경에서 다시 시도해 주세요.');
                } else {
                    alert('시뮬레이션 시작 실패: ' + (res.error || '알 수 없는 오류'));
                }
                return;
            }

            setFloorsUnlocked(true);
            setSimulationId(res.simulationId || res.id);
        } catch (e) {
            if (streamTimeoutRef.current) {
                clearTimeout(streamTimeoutRef.current);
                streamTimeoutRef.current = null;
            }

            setPhase('input');
            setRightCollapsed(false);
            alert('시뮬레이션 시작 실패: ' + e.message);
        }
    }

    async function replaySimulation(opts = {}) {
        if (isMobile) scrollToTopSmooth();

        const r = replayRef.current;

        setPhase('ready');
        setMetric('co');
        setRightCollapsed(true);
        try {
            const useFloor = r?.floor ?? floor;
            const useMetric = r?.metric ?? metric;
            const zi = useFloor;
            const qi = qiFromMetric(useMetric);

            const useX = r.x;
            const useY = r.y;

            const body = {
                x: useX,
                y: useY,
                z: zi,
                q: qi,
                hrr_density: r?.hrr_density ?? hrr,
                thresholds: r?.thresholds ?? params,
                model_id: r?.model_id ?? modelId,
                simulationId: r?.id,
            };

            const res = await fetchJSON(`${API_BASE}/simulation/replay`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });

            setSimulationId(res.simulationId || res.id);
        } catch (e) {
            setPhase('input');
            setRightCollapsed(false);
            alert('시뮬레이션 시작 실패: ' + e.message);
        }
    }

    function resetSimulation() {
        setPhase('input');
        setRightCollapsed(false);
        setSimulationId(null);
        setActiveTab('result');
        setTime(0);
        setIsPlaying(false);
        setSpeed(1);
        setPicked(null);
        autoStartedRef.current = false;
        setFireAlarm(null);
        setRouteGuide(null);

        clearSimState(simKey);
        setFloorsUnlocked(false);
    }

    function scrollToTopSmooth() {
        if (typeof window === 'undefined') return;
        window.scrollTo({
            top: 165,
            left: 0,
            behavior: 'smooth',
        });
    }
    useEffect(() => {
        if (isMobile) scrollToTopSmooth();
    }, []);
    return (
        <div className={`${styles.simPage} ${rightCollapsed ? styles.simPageCollapsed : ''}`} ref={simTopRef}>
            {/* LEFT: FloorPicker + Viewer card */}
            <div className={styles.left}>
                <FloorPicker
                    value={floor}
                    metric={metric}
                    simulationId={simulationId}
                    onChange={(f) => {
                        setOverlayVisible(false);
                        setCurrentTexture(null);
                        setFloor(f);
                        if (simulationId && phase !== 'input') {
                            const zi = ziFromFloor(f);
                            const qi = qiFromMetric(metric);
                            try {
                                wsSend({
                                    action: 'change_contour',
                                    simulationId,
                                    zi,
                                    qi,
                                });
                            } catch {}
                        }
                    }}
                    allowedFloors={floorsUnlocked ? null : LIMITED_FLOORS}
                    send={wsSend}
                />

                <div className={`${styles.viewer} ${styles.card} ${styles.viewerCard}`}>
                    <div className={`${styles.viewerHead} ${phase !== 'input' ? styles.viewerHeadActive : ''}`}>
                        {phase !== 'input' && (
                            <div
                                style={{
                                    fontWeight: 700,
                                    color: 'var(--c-text)',
                                }}
                            >
                                시뮬레이션 결과
                            </div>
                        )}
                    </div>

                    <div className={styles.viewerBody}>
                        {/* LEFT: GLB */}
                        <div className={styles.viewerBodyLeft}>
                            {phase === 'input' && (
                                <div className={styles.viewer3DWrap}>
                                    <FloorViewer floor={floor} onPick={locked ? () => {} : setPicked} overlayTexture={null} overlaySize={{ w: 30, h: 41 }} />
                                </div>
                            )}

                            {phase !== 'input' && activeTab === 'result' && (
                                <>
                                    <div className={styles.metricSwitch}>
                                        {/* 버튼 줄 */}
                                        <div className={styles.metricButtonsRow}>
                                            {METRIC_OPTIONS.map((opt) => (
                                                <button
                                                    key={opt.key}
                                                    className={`${styles.btn} ${styles.btnSm} ${metric === opt.key ? styles.btnActive : ''}`}
                                                    aria-pressed={metric === opt.key}
                                                    onClick={() => {
                                                        setOverlayVisible(false);
                                                        setCurrentTexture(null);
                                                        const zi = ziFromFloor(floor);
                                                        const qi = qiFromMetric(opt.key);
                                                        setMetric(opt.key);
                                                        wsSend({
                                                            action: 'change_contour',
                                                            simulationId,
                                                            zi,
                                                            qi,
                                                        });
                                                    }}
                                                    title={`${opt.label} 보기`}
                                                >
                                                    {opt.label}
                                                </button>
                                            ))}

                                            {/* 🔹 모바일에서만 보이는 레전드 토글 버튼 */}
                                            {isMobile && (
                                                <button
                                                    type="button"
                                                    className={`${styles.btn} ${styles.btnSm} ${legendOpen ? styles.btnActive : ''}`}
                                                    onClick={() => setLegendOpen((v) => !v)}
                                                >
                                                    {legendOpen ? '▲ 숨기기' : '▼ 펼치기'}
                                                </button>
                                            )}
                                        </div>

                                        {/* 범례 영역: PC에서는 항상, 모바일에서는 legendOpen 일 때만 보이도록 CSS로 제어 */}
                                        {!isMobile && (
                                            <div className={`${styles.metricLegend}`}>
                                                <ColorbarLegend
                                                    metric={metric}
                                                    minV={getRangeForMetric(metric, params).minV}
                                                    maxV={getRangeForMetric(metric, params).maxV}
                                                    threshold={metric === 'co' ? params.co : metric === 'temp' ? params.temp : params.vis}
                                                    unit={metric === 'co' ? 'ppm' : metric === 'temp' ? '℃' : 'm'}
                                                    width={16}
                                                    height={160}
                                                    viridis={viridis}
                                                />
                                            </div>
                                        )}
                                    </div>

                                    <div className={styles.viewer3DWrap}>
                                        <BuildingViewer
                                            selectedFloor={floor}
                                            initialMode={viewerMode}
                                            onModeChange={setViewerMode}
                                            onViewChange={setViewerView}
                                            fixedInitialView={viewerView || undefined}
                                            overlayByFloor={{
                                                [floor]: overlayVisible ? currentTexture : null,
                                            }}
                                            overlaySize={{ w: 30, h: 41 }}
                                            showGrid={true}
                                            simulationId={simulationId}
                                            send={wsSend}
                                            routesPayload={routesPayload}
                                            currentMetric={metric}
                                            overlaysByMetric={miniOverlays}
                                            controlledByFloor={controlledByFloor}
                                            renderMeasureRef={renderMeasureRef}
                                            onRouteSelected={(guide) => handleRouteSelection(guide)}
                                            onRouteCleared={() => setRouteGuide(null)}
                                        />
                                        {isMobile && fireAlarm?.message && <FireAlarmBarMobile alarm={fireAlarm} speed={60} onDone={handleFireAlarmDone} />}
                                    </div>

                                    {/* 🔥 화재/통제구역 알림 토스트 */}
                                    {fireAlarm?.message && !isMobile && (
                                        <div className={styles.fireAlarm} role="status" aria-live="assertive">
                                            <div className={styles.fireAlarmHead}>
                                                <div className={styles.fireAlarmDot} />
                                                <div className={styles.fireAlarmTitle}>화재/통제구역 경보</div>
                                            </div>

                                            <div className={styles.fireAlarmMessage}>
                                                {fireAlarm?.message
                                                    .split(/\r?\n/)
                                                    .filter((line) => line.trim().length > 0)
                                                    .map((line, idx) => {
                                                        const m = line.match(/^(지하\d+층|지상\d+층)([-\s]*)(.*)$/);
                                                        if (!m) {
                                                            return <div key={idx}>{line}</div>;
                                                        }
                                                        const [, floorLabel, sep, rest] = m;
                                                        return (
                                                            <div key={idx}>
                                                                <strong>{floorLabel}</strong>
                                                                {sep}
                                                                {rest}
                                                            </div>
                                                        );
                                                    })}
                                            </div>

                                            <div className={styles.fireAlarmFooter}>
                                                <button
                                                    type="button"
                                                    className={styles.fireAlarmBtn}
                                                    onClick={() => {
                                                        alarmActiveRef.current = false;
                                                        setFireAlarm(null);
                                                    }}
                                                >
                                                    닫기
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                    {/* 👇 모바일 전용: BuildingViewer 아래에 미디어 컨트롤 + 탭 그래프 */}
                                    {phase !== 'input' && activeTab === 'result' && isMobile && (
                                        <div className={styles.mobileMediaAndCharts}>
                                            {/* 모바일 전용 미디어 컨트롤 */}
                                            <div className={styles.viewerFootMobile}>
                                                <MediaControls
                                                    time={time}
                                                    setTime={setTime}
                                                    isPlaying={isPlaying}
                                                    setIsPlaying={setIsPlaying}
                                                    speed={speed}
                                                    setSpeed={setSpeed}
                                                    simulationId={simulationId}
                                                    send={send}
                                                />
                                            </div>

                                            {/* 모바일 전용 그래프/경로 탭 */}
                                            <div className={styles.mobileChartsContainer}>
                                                {routeGuide ? (
                                                    <RouteGuidePanel guide={routeGuide} />
                                                ) : (
                                                    <>
                                                        <div className={styles.mobileChartsTabs}>
                                                            <button
                                                                type="button"
                                                                className={`${styles.mobileTab} ${mobileGraphTab === 'co' ? styles.mobileTabActive : ''}`}
                                                                onClick={() => setMobileGraphTab('co')}
                                                            >
                                                                CO농도
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className={`${styles.mobileTab} ${mobileGraphTab === 'temp' ? styles.mobileTabActive : ''}`}
                                                                onClick={() => setMobileGraphTab('temp')}
                                                            >
                                                                온도
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className={`${styles.mobileTab} ${mobileGraphTab === 'vis' ? styles.mobileTabActive : ''}`}
                                                                onClick={() => setMobileGraphTab('vis')}
                                                            >
                                                                가시거리
                                                            </button>{' '}
                                                        </div>

                                                        <div className={styles.mobileChartsBody}>
                                                            {mobileGraphTab === 'co' && (
                                                                <LinePlotGraph
                                                                    title="CO농도 (ppm)"
                                                                    x={xArr}
                                                                    y={coArr}
                                                                    color="#e11d48"
                                                                    yUnit="ppm"
                                                                    time={time}
                                                                    threshold={params.co}
                                                                    compact={true}
                                                                    showTitle={false}
                                                                />
                                                            )}
                                                            {mobileGraphTab === 'temp' && (
                                                                <LinePlotGraph
                                                                    title="온도 (℃)"
                                                                    x={xArr}
                                                                    y={tpArr}
                                                                    color="#2563eb"
                                                                    yUnit="℃"
                                                                    time={time}
                                                                    threshold={params.temp}
                                                                    compact={true}
                                                                    showTitle={false}
                                                                />
                                                            )}
                                                            {mobileGraphTab === 'vis' && (
                                                                <LinePlotGraph
                                                                    title="가시거리 (m)"
                                                                    x={xArr}
                                                                    y={vsArr}
                                                                    color="#10b981"
                                                                    yUnit="m"
                                                                    time={time}
                                                                    threshold={params.vis}
                                                                    compact={true}
                                                                    showTitle={false}
                                                                />
                                                            )}
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        {/* RIGHT: uPlot 기반 3 그래프 / 경로 안내 */}
                        {phase !== 'input' && activeTab === 'result' && (
                            <>
                                {/* PC: 오른쪽 컬럼 */}
                                {!isMobile && (
                                    <div className={styles.viewerBodyRight}>
                                        {routeGuide ? (
                                            <RouteGuidePanel guide={routeGuide} />
                                        ) : (
                                            <>
                                                <LinePlotGraph title="CO농도 (ppm)" x={xArr} y={coArr} color="#e11d48" yUnit="ppm" time={time} threshold={params.co} />
                                                <LinePlotGraph title="온도 (℃)" x={xArr} y={tpArr} color="#2563eb" yUnit="℃" time={time} threshold={params.temp} />
                                                <LinePlotGraph title="가시거리 (m)" x={xArr} y={vsArr} color="#10b981" yUnit="m" time={time} threshold={params.vis} />
                                            </>
                                        )}
                                    </div>
                                )}
                            </>
                        )}

                        {phase === 'running' && (
                            <div
                                style={{
                                    position: 'absolute',
                                    inset: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    background: 'rgba(255,255,255,.55)',
                                    backdropFilter: 'blur(2px)',
                                    zIndex: 4,
                                }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: 12,
                                    }}
                                >
                                    <div className={styles.spinner} aria-label="" />
                                </div>
                            </div>
                        )}
                    </div>

                    {phase !== 'input' && !isMobile && (
                        <div className={styles.viewerFoot}>
                            <MediaControls
                                time={time}
                                setTime={setTime}
                                isPlaying={isPlaying}
                                setIsPlaying={setIsPlaying}
                                speed={speed}
                                setSpeed={setSpeed}
                                simulationId={simulationId}
                                send={send}
                            />
                        </div>
                    )}
                </div>
            </div>
            <div className={styles.gapCol}>
                {showExpandHandle && !isMobile && (
                    <button
                        className={styles.gapToggle}
                        title={rightCollapsed ? '설정 패널 펼치기' : '설정 패널 숨기기'}
                        onClick={() => setRightCollapsed((v) => !v)}
                        aria-label="설정 패널 토글"
                    >
                        <span className={styles.gapToggleChev}>{rightCollapsed ? '◀' : '▶'}</span>
                    </button>
                )}
            </div>
            {/* RIGHT: 설정 패널 */}
            {!(isMobile && phase !== 'input') && ( // 🔹 모바일 + 결과 phase 에서는 숨김
                <div className={styles.right}>
                    <div className={`${styles.card} ${styles.panel} ${styles.settingsPanel}`}>
                        <div className={styles.panelHeader}>시뮬레이션 설정</div>

                        <div className={styles.subhead}>입력 파라메터</div>
                        <div className={styles.settingsGrid}>
                            <div>
                                <div className={styles.label}>좌표</div>
                                <div
                                    className={styles.input}
                                    style={{
                                        height: 40,
                                        display: 'flex',
                                        alignItems: 'center',
                                        marginTop: 4,
                                    }}
                                >
                                    {picked ? `x: ${picked.x.toFixed(2)}, y: ${(-picked.y).toFixed(2)}, 층: ${floor}` : '평면도를 클릭해 좌표를 선택'}
                                </div>
                            </div>

                            <div>
                                <div className={styles.label}>발열량 (kW/m³)</div>
                                <div
                                    style={{
                                        display: 'flex',
                                        gap: 8,
                                        marginTop: 4,
                                    }}
                                >
                                    {HRR_PRESETS.map((v) => (
                                        <button key={v} className={`${styles.btn} ${hrr === v ? styles.btnActive : ''}`} onClick={() => !locked && setHrr(v)} disabled={locked}>
                                            {v}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className={styles.subhead} style={{ marginTop: 12 }}>
                            한계치 설정
                        </div>
                        <div className={styles.thresholdGrid}>
                            <div>
                                <div className={styles.label}>CO농도 (ppm)</div>
                                <input
                                    className={styles.input}
                                    style={{ marginTop: 4 }}
                                    type="number"
                                    value={params.co}
                                    onChange={(e) =>
                                        setParams({
                                            ...params,
                                            co: Number(e.target.value),
                                        })
                                    }
                                    disabled={locked}
                                />
                            </div>
                            <div>
                                <div className={styles.label}>온도 (℃)</div>
                                <input
                                    className={styles.input}
                                    style={{ marginTop: 4 }}
                                    type="number"
                                    value={params.temp}
                                    onChange={(e) =>
                                        setParams({
                                            ...params,
                                            temp: Number(e.target.value),
                                        })
                                    }
                                    disabled={locked}
                                />
                            </div>
                            <div>
                                <div className={styles.label}>가시거리 (m)</div>
                                <input
                                    className={styles.input}
                                    style={{ marginTop: 4 }}
                                    type="number"
                                    value={params.vis}
                                    onChange={(e) =>
                                        setParams({
                                            ...params,
                                            vis: Number(e.target.value),
                                        })
                                    }
                                    disabled={locked}
                                />
                            </div>
                        </div>

                        {/* 실행 / 재설정 버튼 – PC: 패널 하단, 모바일: 화면 하단 고정 */}
                        <div className={styles.settingsActions}>
                            <button className={styles.btn} onClick={resetSimulation} disabled={!locked}>
                                재설정
                            </button>
                            <button className={`${styles.btn} ${styles.btnPrimary}`} onClick={() => startSimulation()} disabled={locked || !picked}>
                                실행
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

/* ---------------- RouteGuide 유틸 ---------------- */

function groupStepsByFloor(steps = []) {
    const groups = [];
    let current = null;

    for (const st of steps) {
        const floorKey = st.floor ?? '';

        if (!current || current.floor !== floorKey) {
            current = { floor: floorKey, steps: [] };
            groups.push(current);
        }
        current.steps.push(st);
    }
    return groups;
}

function stripFloorPrefix(label = '') {
    if (!label) return '';
    return label.replace(/^(지상|지하|극장)?\d+층?[.\s_-]*/u, '').trim();
}

function RouteGuidePanel({ guide }) {
    if (!guide) return null;
    const { startLabel, endLabel, totalDistance, steps = [] } = guide;

    const groups = groupStepsByFloor(steps);

    const displayStartLabel = stripFloorPrefix(startLabel);
    const displayEndLabel = stripFloorPrefix(endLabel);

    return (
        <div className={styles.rgCard}>
            {/* 상단 요약 영역 */}
            <div className={styles.rgHeader}>
                <div>
                    <div className={styles.rgTitle}>이동 경로 안내</div>
                    <div className={styles.rgMeta}>
                        <span className={styles.rgPill}>출발</span>
                        <span className={styles.rgMetaText}>{displayStartLabel || '-'}</span>
                        <span className={styles.rgArrow}>→</span>
                        <span className={`${styles.rgPill} ${styles.rgPillDest}`}>도착</span>
                        <span className={styles.rgMetaText}>{displayEndLabel || '-'}</span>
                    </div>
                    <div className={styles.rgTotal}>
                        총 이동 거리&nbsp;
                        <strong>{(totalDistance || 0).toFixed(1)} m</strong>
                    </div>
                </div>
            </div>

            {/* 본문 래퍼 + 그룹 리스트 */}
            <div className={styles.rgBody}>
                <ol className={styles.rgGroups}>
                    {groups.map((g, idx) => {
                        const isStair = g.floor === '-1';
                        const floorLabel = isStair ? '계단 이동' : `${g.floor}층 이동`;
                        const groupDist = g.steps.reduce((sum, st) => sum + (st.distance || 0), 0);

                        return (
                            <li key={idx} className={styles.rgGroup}>
                                {/* 번호 + 층 이동 요약 */}
                                <div className={styles.rgGroupHeadline}>
                                    <div className={styles.rgGroupIndex}>{idx + 1}</div>
                                    <div className={styles.rgGroupHeadlineText}>
                                        <span className={`${styles.rgFloorChip} ${isStair ? styles.rgFloorChipStair : ''}`}>{floorLabel}</span>
                                        <span className={styles.rgGroupDistance}>{groupDist.toFixed(1)} m</span>
                                    </div>
                                </div>

                                {/* 해당 층의 세부 이동 카드들 */}
                                <ul className={styles.rgSubsteps}>
                                    {g.steps.map((st, i) => (
                                        <li key={i} className={styles.rgSubstepCard}>
                                            <div className={styles.rgSubstepMain}>
                                                <span className={styles.rgSubstepName}>{stripFloorPrefix(st.sectionName) || '통로 이동'}</span>
                                                <span className={styles.rgSubstepDistance}>{(st.distance || 0).toFixed(1)} m</span>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </li>
                        );
                    })}
                </ol>
            </div>
        </div>
    );
}

function FireAlarmBarMobile({ alarm, speed = 80, onDone }) {
    const { id, message } = alarm;
    const containerRef = useRef(null);
    const textRef = useRef(null);
    const [styleVars, setStyleVars] = useState({});

    useEffect(() => {
        if (!containerRef.current || !textRef.current || !message) return;

        const containerWidth = containerRef.current.clientWidth;
        const textWidth = textRef.current.scrollWidth;

        const start = containerWidth; // 오른쪽 밖
        const distance = containerWidth + textWidth; // 오른쪽 밖 → 왼쪽 밖

        let duration = distance / speed; // px/s → s
        const MIN_DURATION = 5;
        const MAX_DURATION = 40;
        if (duration < MIN_DURATION) duration = MIN_DURATION;
        if (duration > MAX_DURATION) duration = MAX_DURATION;

        // CSS 변수 설정
        setStyleVars({
            '--marquee-start': `${start}px`,
            '--marquee-distance': `${distance}px`,
            '--marquee-duration': `${duration}s`,
        });

        // 🔹 텍스트가 완전히 왼쪽으로 사라지는 실제 시간 계산
        // 비율: (C + T) / (2C + T)
        const exitRatio = (containerWidth + textWidth) / (2 * containerWidth + textWidth);
        const exitDuration = duration * exitRatio;

        // 약간의 여유 (0.2초 정도) 두고 배경 제거
        const effectiveDuration = Math.max(0, exitDuration - 0.2) + 1;

        const timer = setTimeout(() => {
            if (typeof onDone === 'function') {
                onDone();
            }
        }, effectiveDuration * 1000);

        return () => clearTimeout(timer);
    }, [id, message, speed, onDone]);

    return (
        <div className={styles.fireAlarmMobile} ref={containerRef} style={{ pointerEvents: 'none' }}>
            <div className={styles.fireAlarmMobileInner}>
                <span key={id} className={styles.fireAlarmMobileText} ref={textRef} style={styleVars}>
                    {message}
                </span>
            </div>
        </div>
    );
}
